import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { useApiClient } from '../useApiClient'
import {
  listCollectionsView,
  listCollectionPins,
  pinToCollection,
  unpinFromCollection,
  createCollection,
  renameCollection,
  toggleDashboard,
  deleteCollection,
  editPin,
  movePinUp,
  movePinDown,
} from '../endpoints/collections'
import type { VCollection, CollectionPinResult } from '../endpoints/collections'
import type { PinV1 } from '~/pin/PinV1'

function swapAdjacent(
  arr: CollectionPinResult[],
  index: number,
  direction: 'up' | 'down',
): CollectionPinResult[] {
  const target = direction === 'up' ? index - 1 : index + 1
  if (target < 0 || target >= arr.length) return arr
  const out = [...arr]
  ;[out[index], out[target]] = [out[target]!, out[index]!]
  return out
}

export function useOwnedCollections() {
  const client = useApiClient()
  const { user } = useAuth0()
  const userId = user?.sub
  return useQuery({
    queryKey: ['case_collection', 'owned'] as const,
    queryFn: () => listCollectionsView(client),
    enabled: !!userId,
    select: (rows: VCollection[]) => rows.filter((r) => r.user_id === userId),
  })
}

export function useSharedCollections() {
  const client = useApiClient()
  const { user } = useAuth0()
  const userId = user?.sub
  return useQuery({
    queryKey: ['case_collection', 'owned'] as const,
    queryFn: () => listCollectionsView(client),
    enabled: !!userId,
    select: (rows: VCollection[]) => rows.filter((r) => r.user_id !== userId),
  })
}

export function useCollectionPins(collectionId: string | null) {
  const client = useApiClient()
  return useQuery({
    queryKey: ['graph_collection', collectionId] as const,
    queryFn: () => listCollectionPins(client, collectionId!),
    enabled: collectionId !== null,
  })
}

export function usePinToCollection() {
  const client = useApiClient()
  const qc = useQueryClient()
  const { user } = useAuth0()
  return useMutation({
    mutationFn: ({ collectionId, pin }: { collectionId: string; pin: PinV1 }) => {
      if (!user?.sub) throw new Error('usePinToCollection: not authenticated')
      return pinToCollection(client, user.sub, collectionId, pin)
    },
    onSuccess: (_, { collectionId }) =>
      qc.invalidateQueries({ queryKey: ['graph_collection', collectionId] }),
  })
}

export function useUnpin() {
  const client = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ pinId }: { pinId: string; collectionId: string }) =>
      unpinFromCollection(client, pinId),
    onSuccess: (_, { collectionId }) =>
      qc.invalidateQueries({ queryKey: ['graph_collection', collectionId] }),
  })
}

export function useCreateCollection() {
  const client = useApiClient()
  const qc = useQueryClient()
  const { user } = useAuth0()
  return useMutation({
    mutationFn: ({ name, isDashboard }: { name: string; isDashboard: boolean }) => {
      if (!user?.sub) throw new Error('useCreateCollection: not authenticated')
      return createCollection(client, user.sub, name, isDashboard)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['case_collection', 'owned'] }),
  })
}

export function useRenameCollection() {
  const client = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ collectionId, name }: { collectionId: string; name: string }) =>
      renameCollection(client, collectionId, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['case_collection', 'owned'] }),
  })
}

export function useToggleDashboard() {
  const client = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ collectionId, isDashboard }: { collectionId: string; isDashboard: boolean }) =>
      toggleDashboard(client, collectionId, isDashboard),
    onMutate: async ({ collectionId, isDashboard }) => {
      const key = ['case_collection', 'owned'] as const
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<VCollection[]>(key)
      qc.setQueryData<VCollection[]>(key, (old) =>
        (old ?? []).map((c) => (c.id === collectionId ? { ...c, is_dashboard: isDashboard } : c)),
      )
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['case_collection', 'owned'], ctx.previous)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['case_collection', 'owned'] }),
  })
}

export function useDeleteCollection() {
  const client = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ collectionId }: { collectionId: string }) =>
      deleteCollection(client, collectionId),
    onSuccess: (_, { collectionId }) => {
      qc.invalidateQueries({ queryKey: ['case_collection', 'owned'] })
      qc.invalidateQueries({ queryKey: ['graph_collection', collectionId] })
    },
  })
}

export function useEditPin() {
  const client = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      pinId,
      pin,
      customNotes,
    }: {
      pinId: string
      collectionId: string
      pin: PinV1
      customNotes: string | null
    }) => editPin(client, pinId, pin, customNotes),
    onSuccess: (_, { collectionId }) =>
      qc.invalidateQueries({ queryKey: ['graph_collection', collectionId] }),
  })
}

export function useMovePinUp() {
  const client = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ pinId }: { pinId: string; collectionId: string }) => movePinUp(client, pinId),
    onMutate: async ({ pinId, collectionId }) => {
      const key = ['graph_collection', collectionId] as const
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<CollectionPinResult[]>(key)
      if (previous) {
        const idx = previous.findIndex((r) => r.row.id === pinId)
        if (idx > 0) qc.setQueryData(key, swapAdjacent(previous, idx, 'up'))
      }
      return { previous, key }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.key, ctx.previous)
    },
    onSettled: (_data, _err, { collectionId }) =>
      qc.invalidateQueries({ queryKey: ['graph_collection', collectionId] }),
  })
}

export function useMovePinDown() {
  const client = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ pinId }: { pinId: string; collectionId: string }) => movePinDown(client, pinId),
    onMutate: async ({ pinId, collectionId }) => {
      const key = ['graph_collection', collectionId] as const
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<CollectionPinResult[]>(key)
      if (previous) {
        const idx = previous.findIndex((r) => r.row.id === pinId)
        if (idx >= 0 && idx < previous.length - 1)
          qc.setQueryData(key, swapAdjacent(previous, idx, 'down'))
      }
      return { previous, key }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.key, ctx.previous)
    },
    onSettled: (_data, _err, { collectionId }) =>
      qc.invalidateQueries({ queryKey: ['graph_collection', collectionId] }),
  })
}
